import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { normalizeRole } from '../utils/roles';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  // Helper to get normalized role from localStorage. Funnel through
  // normalizeRole so any variations collapse to the canonical form.
  const getInitialRole = () => {
    const raw = localStorage.getItem('roleName') || localStorage.getItem('role') || null;
    return raw ? normalizeRole(raw) : null;
  };

  const getInitialCenterId = () => {
    const cid = localStorage.getItem('centerId');
    return cid ? parseInt(cid) : null;
  };

  const [permissions, setPermissions] = useState(null);
  const [resourcePermissions, setResourcePermissions] = useState(null);
  const [resources, setResources] = useState([]);
  const [userRole, setUserRole] = useState(getInitialRole);
  const [centerId, setCenterId] = useState(getInitialCenterId);
  // True for accounts onboarded from an accepted public-link application — the
  // frontend surfaces them as "User" (restricted sidebar) vs real founders.
  const [isPublicUser, setIsPublicUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setPermissions(null);
        setResourcePermissions(null);
        setResources([]);
        setUserRole(null);
        setCenterId(null);
        setIsPublicUser(false);
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Both endpoints are role-derived. Legacy flags come from /auth/permissions;
      // the resource matrix used for RBAC gating comes from /auth/me/permissions.
      const [legacyResp, matrixResp] = await Promise.all([
        fetch(`${API_BASE_URL}/auth/permissions`, { method: 'GET', headers }),
        fetch(`${API_BASE_URL}/auth/me/permissions`, { method: 'GET', headers }),
      ]);

      if (legacyResp.status === 401 || matrixResp.status === 401) {
        localStorage.clear();
        setPermissions(null);
        setResourcePermissions(null);
        setResources([]);
        setUserRole(null);
        setCenterId(null);
        setIsPublicUser(false);
        window.location.href = '/login';
        return;
      }

      if (legacyResp.ok) {
        const data = await legacyResp.json();
        if (data.success) {
          setPermissions(data.data.permissions);
          setUserRole(normalizeRole(data.data.role));
          setCenterId(data.data.center_id);
        }
      }

      if (matrixResp.ok) {
        const data = await matrixResp.json();
        if (data.success) {
          setResourcePermissions(data.data.permissions || {});
          setResources(data.data.resources || []);
          setIsPublicUser(Boolean(data.data.is_public_user));
          if (!userRole) setUserRole(normalizeRole(data.data.role));
          setError(null);
        } else {
          setError(data.message || 'Failed to fetch permissions');
        }
      } else {
        setError('Failed to fetch permissions');
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Network error while fetching permissions');
    } finally {
      setLoading(false);
    }
  };

  // Mount-only: fetch permissions on first render, then refetch whenever
  // a `login`/`logout` window event fires. The empty deps array is
  // intentional — `fetchPermissions` is redeclared every render and
  // adding it would cause an infinite refetch loop. The function reads
  // the closed-over `userRole` to decide whether to set it from the
  // matrix response (line ~88), which is acceptable here because the
  // listeners are only fired on auth transitions, when state is stable.
  useEffect(() => {
    fetchPermissions();

    const handleAuthChange = () => {
      fetchPermissions();
    };

    window.addEventListener('login', handleAuthChange);
    window.addEventListener('logout', handleAuthChange);

    return () => {
      window.removeEventListener('login', handleAuthChange);
      window.removeEventListener('logout', handleAuthChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to check if user has a specific permission
  const hasPermission = (permission) => {
    if (!permissions) return false;
    return permissions[permission] === true;
  };

  // Helper function to check if user has any of the specified permissions
  const hasAnyPermission = (permissionList) => {
    if (!permissions || !Array.isArray(permissionList)) return false;
    return permissionList.some(permission => permissions[permission] === true);
  };

  // Helper function to check if user has all specified permissions
  const hasAllPermissions = (permissionList) => {
    if (!permissions || !Array.isArray(permissionList)) return false;
    return permissionList.every(permission => permissions[permission] === true);
  };

  // RBAC: resource × {read, write} check. Superadmin already gets all-true from
  // the backend, so this stays a simple lookup with no special-casing.
  // write implies read so a callsite that asks for read on a write-only role still passes.
  const can = (resource, action = 'read') => {
    if (!resourcePermissions) return false;
    const entry = resourcePermissions[resource];
    if (!entry) return false;
    if (action === 'read') return Boolean(entry.read) || Boolean(entry.write);
    return Boolean(entry[action]);
  };

  const value = {
    permissions,
    resourcePermissions,
    resources,
    userRole,
    centerId,
    isPublicUser,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    refetchPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
